<div class="space-y-4">
  <app-breadcrumbs [items]="breadcrumbItems" />

  <section class="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
    <div class="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 pb-4">
      <div>
        <h1 class="text-xl font-semibold text-slate-900">Create User</h1>
        <p class="mt-1 text-sm text-slate-500">Create login credentials for a new user account.</p>
      </div>

      <button
        type="button"
        (click)="goBackToUsers()"
        class="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
      >
        Back to Users
      </button>
    </div>

    <form [formGroup]="form" (ngSubmit)="submit()" class="mt-5 grid gap-5 lg:grid-cols-2">
      <section class="space-y-4 rounded-xl border border-slate-200 p-4">
        <h2 class="text-base font-semibold text-slate-900">User Details</h2>

        <div>
          <label class="mb-1 block text-sm font-medium text-slate-700">Full Name</label>
          <input
            type="text"
            formControlName="fullName"
            class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
          />
          @if (form.controls.fullName.touched && form.controls.fullName.errors?.['required']) {
            <p class="mt-1 text-xs text-red-600">Full name is required.</p>
          }
        </div>

        <div>
          <label class="mb-1 block text-sm font-medium text-slate-700">Email</label>
          <input
            type="email"
            formControlName="email"
            class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
          />
          @if (form.controls.email.touched && form.controls.email.errors?.['required']) {
            <p class="mt-1 text-xs text-red-600">Email is required.</p>
          }
          @if (form.controls.email.touched && form.controls.email.errors?.['email']) {
            <p class="mt-1 text-xs text-red-600">Enter a valid email address.</p>
          }
        </div>

        <div>
          <label class="mb-1 block text-sm font-medium text-slate-700">Role</label>
          <select
            formControlName="role"
            class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
          >
            <option value="admin">Admin</option>
            <option value="dekalb">Dekalb</option>
            <option value="partner">Partner</option>
          </select>
        </div>

        @if (isPartnerRole()) {
          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700">Partner Organization</label>
            <select
              formControlName="partnerOrganizationId"
              class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
            >
              <option value="">Select a partner organization</option>
              @for (partner of partners(); track partner.id) {
                <option [value]="partner.id">{{ partner.name }}</option>
              }
            </select>
            @if (form.controls.partnerOrganizationId.touched && form.controls.partnerOrganizationId.errors?.['required']) {
              <p class="mt-1 text-xs text-red-600">Partner organization is required for partner users.</p>
            }
            @if (loadingPartners()) {
              <p class="mt-1 text-xs text-slate-500">Loading partner options...</p>
            }
          </div>
        }
      </section>

      <section class="space-y-4 rounded-xl border border-slate-200 p-4">
        <h2 class="text-base font-semibold text-slate-900">Set Password</h2>

        <div>
          <label class="mb-1 block text-sm font-medium text-slate-700">Password</label>
          <input
            type="password"
            formControlName="password"
            class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
          />
          <p class="mt-1 text-xs text-slate-500">Must include at least 8 characters, uppercase, lowercase, and a number.</p>
          @if (form.controls.password.touched && form.controls.password.errors?.['required']) {
            <p class="mt-1 text-xs text-red-600">Password is required.</p>
          }
          @if (form.controls.password.touched && form.controls.password.errors?.['minlength']) {
            <p class="mt-1 text-xs text-red-600">Password must be at least 8 characters.</p>
          }
          @if (form.controls.password.touched && form.controls.password.errors?.['pattern']) {
            <p class="mt-1 text-xs text-red-600">Password must include uppercase, lowercase, and a number.</p>
          }
        </div>

        <div>
          <label class="mb-1 block text-sm font-medium text-slate-700">Confirm Password</label>
          <input
            type="password"
            formControlName="confirmPassword"
            class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
          />
          @if (form.controls.confirmPassword.touched && form.controls.confirmPassword.errors?.['required']) {
            <p class="mt-1 text-xs text-red-600">Confirm password is required.</p>
          }
          @if (form.errors?.['passwordMismatch']) {
            <p class="mt-1 text-xs text-red-600">Passwords do not match.</p>
          }
        </div>
      </section>

      <div class="lg:col-span-2 flex justify-end gap-2">
        <button
          type="button"
          (click)="goBackToUsers()"
          class="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          [disabled]="creating()"
          class="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {{ creating() ? 'Creating...' : 'Create User' }}
        </button>
      </div>
    </form>
  </section>
</div>
